import { useEffect, useState } from 'react';

type UseTabHash<T extends string> = {
  tabHash: T;
  setTabHash: (v: T) => void;
  onTabChange: (_: React.SyntheticEvent | null, v: T) => void;
};

export const useTabHash = <T extends string>(
  tabs: ReadonlyArray<T>,
  defaultValue: T,
): UseTabHash<T> => {
  const hashValue = window.location.hash.slice(1) as any;
  const initialValue = tabs.includes(hashValue) ? hashValue : defaultValue;
  const [tabHash, setTabHash] = useState<T>(initialValue);
  useEffect(() => {
    // TODO fix that hack when react-router is bumped
    if (window.location.hash !== '#' + tabHash)
      window.history.replaceState(
        null,
        '',
        window.location.pathname + '#' + tabHash,
      );
  }, [tabHash]);

  return {
    tabHash,
    setTabHash,
    onTabChange: (_: React.SyntheticEvent | null, v: T) => setTabHash(v),
  };
};

const getStoredTab = <T>(tabs: Array<T>, def: T, storageKey: string): T => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const startKey: any = localStorage.getItem(storageKey);
  return tabs.includes(startKey) ? startKey : def;
};

export const usePersistedTabHash = <T extends string>(
  tabs: Array<T>,
  defaultValue: T,
  storageKey: string,
): UseTabHash<T> => {
  const storedValue = getStoredTab(tabs, defaultValue, storageKey);
  const t = useTabHash(tabs, storedValue);
  const setTabHash = (v: T) => {
    t.setTabHash(v);
    localStorage.setItem(storageKey, v);
  };
  return {
    tabHash: t.tabHash,
    setTabHash,
    onTabChange: (_, v) => setTabHash(v),
  };
};
